import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { VendorErrorCodeResponseDto } from '@evimo/client/models';
import { UnresolvedVendorErrorCodeStatus } from '@evimo/types/enums/unresolved-vendor-error-code-status';
import { VendorErrorSource } from '@evimo/types/enums/vendor-error-source';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import {
  CreatedAtSortHeader,
  type CreatedAtSortOrder,
} from '~/app/components/errorManagement/CreatedAtSortHeader/CreatedAtSortHeader';
import {
  ACTION_ICON_COLORS,
  ACTIONS_CLUSTER_WIDTH,
  renderCenteredActions,
  renderDescriptionCell,
  renderErrorCodeCell,
  renderInfoCell,
  renderVendorErrorCodeCell,
  withPointerCursor,
} from '~/app/components/errorManagement/tableCellRenderers';

const MANUFACTURER_COLUMN_WIDTH = 150;
const ERROR_CODE_COLUMN_WIDTH = 140;
const VENDOR_ERROR_CODE_COLUMN_WIDTH = 180;
const INFO_COLUMN_WIDTH = 200;
const ACTIONS_COLUMN_WIDTH = ACTIONS_CLUSTER_WIDTH + 24;

type UseErrorManagementColumnsArgs = {
  sortOrder: CreatedAtSortOrder;
  onToggleSortOrder: () => void;
  onView: (record: VendorErrorCodeResponseDto) => void;
  onEdit: (record: VendorErrorCodeResponseDto) => void;
  onDelete: (record: VendorErrorCodeResponseDto) => void;
  onReopen: (record: VendorErrorCodeResponseDto) => void;
};

export function useErrorManagementColumns({
  sortOrder,
  onToggleSortOrder,
  onView,
  onEdit,
  onDelete,
  onReopen,
}: UseErrorManagementColumnsArgs): ColumnsType<VendorErrorCodeResponseDto> {
  const { t } = useTranslation('errorManagement');

  return withPointerCursor<VendorErrorCodeResponseDto>([
    {
      title: t('columns.manufacturer'),
      dataIndex: ['manufacturer', 'name'],
      key: 'manufacturer',
      width: MANUFACTURER_COLUMN_WIDTH,
      ellipsis: true,
      render: (_value, record) => record.manufacturer?.name ?? '—',
    },
    {
      title: (
        <CreatedAtSortHeader
          label={t('columns.errorCode')}
          sortOrder={sortOrder}
          onToggle={onToggleSortOrder}
        />
      ),
      dataIndex: 'errorCode',
      key: 'errorCode',
      width: ERROR_CODE_COLUMN_WIDTH,
      onHeaderCell: () => ({ style: { overflow: 'visible' } }),
      render: (value: string) => renderErrorCodeCell(value),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('columns.vendorErrorCode')}
        </span>
      ),
      dataIndex: 'vendorErrorCode',
      key: 'vendorErrorCode',
      width: VENDOR_ERROR_CODE_COLUMN_WIDTH,
      ellipsis: true,
      render: (value: string | null | undefined) =>
        renderVendorErrorCodeCell(value),
    },
    {
      title: t('columns.info'),
      dataIndex: 'info',
      key: 'info',
      width: INFO_COLUMN_WIDTH,
      ellipsis: true,
      render: (value: string | null | undefined) =>
        renderInfoCell(value, 'fill'),
    },
    {
      title: t('columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value: string | null | undefined) =>
        renderDescriptionCell(value, 'fill'),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      width: ACTIONS_COLUMN_WIDTH,
      align: 'center',
      render: (_value, record) => {
        const isQueueOriginated =
          record.source === VendorErrorSource.UNRESOLVED_QUEUE;
        const linked = record.linkedUnresolved;
        const canReopen =
          isQueueOriginated &&
          linked != null &&
          linked.status === UnresolvedVendorErrorCodeStatus.RESOLVED &&
          typeof linked.version === 'number';

        return renderCenteredActions(
          <Space size={2} wrap={false}>
            <Tooltip title={t('actions.view')}>
              <Button
                type="text"
                size="small"
                aria-label={t('actions.view')}
                icon={
                  <EyeOutlined style={{ color: ACTION_ICON_COLORS.view }} />
                }
                onClick={() => onView(record)}
              />
            </Tooltip>
            <Tooltip title={t('actions.edit')}>
              <Button
                type="text"
                size="small"
                aria-label={t('actions.edit')}
                icon={
                  <EditOutlined style={{ color: ACTION_ICON_COLORS.edit }} />
                }
                onClick={() => onEdit(record)}
              />
            </Tooltip>
            {isQueueOriginated ? (
              <Tooltip
                title={
                  canReopen
                    ? t('actions.reopen')
                    : t('messages.reopenMissingLink')
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={t('actions.reopen')}
                  disabled={!canReopen}
                  icon={
                    <RollbackOutlined
                      style={{ color: ACTION_ICON_COLORS.reopen }}
                    />
                  }
                  onClick={() => onReopen(record)}
                />
              </Tooltip>
            ) : null}
            <Tooltip title={t('actions.delete')}>
              <Button
                type="text"
                size="small"
                aria-label={t('actions.delete')}
                icon={
                  <DeleteOutlined
                    style={{ color: ACTION_ICON_COLORS.delete }}
                  />
                }
                onClick={() => onDelete(record)}
              />
            </Tooltip>
          </Space>,
        );
      },
    },
  ]);
}
